import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, CalendarBlank, MapPin, Users } from "@phosphor-icons/react";

export default function Events() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const newOne = () => setEditing({ name: "", date: "", location: "", notes: "" });
  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing.id) await api.put(`/events/${editing.id}`, editing);
      else await api.post("/events", editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"? Contacts won't be deleted, just unlinked.`)) return;
    await api.delete(`/events/${ev.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="events-page">
      <Toaster theme="dark" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// events</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Where you met them</h1>
          <p className="mt-2 text-[#C7DDE5] max-w-2xl">Pre-create event folders before a networking event. Tag contacts as you scan so your follow-ups can reference exactly where you connected.</p>
        </div>
        <button onClick={newOne} data-testid="new-event-btn" className="btn-primary flex items-center gap-2"><Plus weight="bold" /> New event</button>
      </div>

      {editing && (
        <div className="border p-6 mb-8" style={{ borderColor: "var(--accent)" }} data-testid="event-editor">
          <div className="overline mb-4" style={{ color: "var(--accent)" }}>// {editing.id ? "edit event" : "new event"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-base">Name *</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-base" data-testid="event-name" placeholder="e.g. SaaStr 2026" />
            </div>
            <div>
              <label className="label-base">Date</label>
              <input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="input-base"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px", colorScheme: "dark" }} data-testid="event-date" />
            </div>
            <div className="md:col-span-2">
              <label className="label-base">Location</label>
              <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="input-base" data-testid="event-location" placeholder="e.g. Moscone West, San Francisco" />
            </div>
            <div className="md:col-span-2">
              <label className="label-base">Notes</label>
              <textarea rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="input-base"
                style={{ border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="event-notes" placeholder="Goals, booth #, who you wanted to meet..." />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} data-testid="save-event-btn" className="btn-primary flex items-center gap-2"><FloppyDisk weight="bold" /> Save</button>
            <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 && !editing ? (
        <div className="border p-10 text-center" style={{ borderColor: "var(--border)" }}>
          <CalendarBlank size={48} weight="duotone" color="#2DD4D4" className="mx-auto" />
          <p className="mt-4 text-[#C7DDE5]">No events yet. Create one before your next networking event.</p>
          <button onClick={newOne} className="btn-primary mt-6 inline-flex items-center gap-2"><Plus weight="bold" /> Create your first event</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
          {items.map((ev) => (
            <div key={ev.id} className="border-r border-b p-6 group hover:bg-[#15303A]" style={{ borderColor: "var(--border)" }} data-testid={`event-card-${ev.id}`}>
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-xl truncate">{ev.name}</h3>
                  {ev.date && <div className="overline mt-2 flex items-center gap-2"><CalendarBlank weight="bold" /> {new Date(ev.date).toLocaleDateString()}</div>}
                  {ev.location && <div className="overline mt-1 flex items-center gap-2"><MapPin weight="bold" /> {ev.location}</div>}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => setEditing(ev)} data-testid={`edit-event-${ev.id}`} className="p-2 hover:text-white"><PencilSimple weight="bold" /></button>
                  <button onClick={() => remove(ev)} data-testid={`del-event-${ev.id}`} className="p-2 hover:text-white"><Trash weight="bold" /></button>
                </div>
              </div>
              {ev.notes && <p className="mt-4 text-sm text-[#C7DDE5] line-clamp-3">{ev.notes}</p>}
              <div className="mt-6 pt-4 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                <span className="tag flex items-center gap-2"><Users weight="bold" /> {ev.contact_count} contact{ev.contact_count === 1 ? "" : "s"}</span>
                <Link to={`/contacts?event_id=${ev.id}`} data-testid={`view-event-contacts-${ev.id}`} className="overline hover:text-white">view →</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
